<script setup>
  import { computed, shallowRef } from 'vue';
  import {
    DocumentTextIcon,
    PhotoIcon,
    VideoCameraIcon,
    DocumentIcon,
  } from '@heroicons/vue/24/outline';
  import MediaPreviewModal from './MediaPreviewModal.vue';


  const props = defineProps({
    file: {
      type: Object,
      required: true
    },
    isNew: {
      type: Boolean,
      default: false
    },
    isSaved: {
      type: Boolean,
      default: false
    },
    willBeDeleted: {
      type: Boolean,
      default: false
    }
  });

  const fileType = computed(()=>{
    let {mime_type=''} = props.file;
    if(mime_type.includes('image')){
      return 'Image';
    } if(mime_type.includes('doc')){
      return 'Word Document';
    } if(mime_type.includes('text')){
      return 'Text File';
    } if(mime_type.includes('video')){
      return 'Video';
    } if(mime_type.includes('pdf')){
      return 'PDF';
    }
  });

  const previewModalOpen = shallowRef(false);

</script>

<template>
  <li class="flex shadow-sm rounded-md mb-2 relative">
    <MediaPreviewModal
      :show="previewModalOpen"
      :file="file"
      @close="previewModalOpen = false"
    />
    <button
      type="button"
      @click="previewModalOpen = true"
    >
      <div class="cursor-pointer relative shrink-0 flex items-center justify-center w-16 h-16 bg-indigo-600 hover:bg-indigo-800 text-white text-sm leading-5 font-medium rounded-l-md">
        <svg
          v-if="willBeDeleted"
          class="absolute text-red-600 w-full h-full"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path fill-rule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clip-rule="evenodd"/>
        </svg>

        <img
          class="h-full w-full rounded-l-md hover:border-2 border-indigo-300 object-cover"
          v-if="file.thumbnail || file.preview_url"
          :src="file.thumbnail || file.preview_url"
          alt="thumbnail"
        >
        <div v-else class="text-indigo-100 w-full hover:text-indigo-200">
          <PhotoIcon v-if="fileType === 'Image'" class="w-10 h-10 m-auto" />
          <DocumentTextIcon v-else-if="fileType === 'Text File'" class="w-10 h-10 m-auto" />
          <DocumentIcon v-else-if="fileType === 'Word Document'" class="w-10 h-10 m-auto" />
          <VideoCameraIcon v-else-if="fileType === 'Video'" class="w-10 h-10 m-auto" />
          <DocumentTextIcon v-else-if="fileType === 'PDF'" class="w-10 h-10 m-auto" />
          <DocumentIcon v-else class="w-10 h-10 m-auto" />
        </div>
      </div>
    </button>

    <div class="flex-1 flex items-center justify-between border-t border-r border-b border-gray-200 bg-white rounded-r-md truncate">
      <div class="flex-1 px-4 py-2 text-sm leading-5 truncate">
        {{file.name}}
        <span
          v-if="isNew"
          class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium leading-4 bg-yellow-100 text-yellow-800"
        >
          New
        </span>
        <span
          v-else-if="willBeDeleted"
          class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium leading-4 bg-red-100 text-red-800"
        >
          To Be Deleted
        </span>
        <span
          v-else-if="isSaved"
          class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium leading-4 bg-green-100 text-green-800"
        >
          Saved
        </span>
        <p class="text-gray-500">
          {{fileType}}
        </p>
      </div>
      <div class="shrink-0 pr-2">
        <slot name="actions"></slot>
      </div>
    </div>
  </li>
</template>


